import { BillReceiverType } from "../../organization/userGroups/userGroupsTypes"

export enum BillReceiversType {
    PRIVATE_PERSON = "PRIVATE_PERSON",
    PUBLIC_INSTITUTION = "PUBLIC_INSTITUTION",
    PRIVATE_COMPANY = "PRIVATE_COMPANY",
}

export type BillTypeMap<T> = {
    [key in BillReceiversType]: T
}

export enum CountryCode {
    DA = "da",
}

export type SendInvoiceCompany = Readonly<{
    invoiceType: BillReceiverType
    search: string
}>

export type BillReceiversItem = Readonly<{
    id: number
    type: string
    debitorNo: string
    name: string
    addressDisplayText: string
    endpoint: EndpointEan | EndpointEmail
    isActive: boolean
    cvrNumber?: string
}>

type EndpointEan = Readonly<{
    type: BillReceiverType.EAN_NUMBER
    eanNumber: string
}>
type EndpointEmail = Readonly<{
    type: BillReceiverType.EMAIL_ADDRESS
    email: string
}>

export type BaseFormValues = Readonly<{
    country: CountryCode
    debtorNumber: string
    addressLine1: string
    addressLine2: string
    postalCode: string
    city: string
    telephone: string
}>

export type PrivatePersonDetailsFormValues = Readonly<
    BaseFormValues & {
        name: string
    }
>

export type PublicInstitutionDetailsFormValues = Readonly<
    BaseFormValues & {
        institutionName: string
        email: string
        contactPerson: string
    }
>

export type PrivateCompanyDetailsFormValues = Readonly<
    BaseFormValues & {
        companyName: string
        email: string
        contactPerson: string
    }
>

export type BillReceiversFormValuesType =
    | PrivatePersonDetailsFormValues
    | PublicInstitutionDetailsFormValues
    | PrivateCompanyDetailsFormValues

type Country = {
    countryCode: string
}

export type BillReceiverAddress = {
    country: Country
    postalCode: string
    city: string
    addressLines: string[]
}

type EmailEndpoint = {
    type?: BillReceiverType.EMAIL_ADDRESS
    email?: string
}

type EanEndpoint = {
    type?: BillReceiverType.EAN_NUMBER
    eanNumber?: string
}

export type BillReceiverEndpoint = EmailEndpoint | EanEndpoint

type Contact = {
    name: string
    email: string
    phone: string
}

export type PrivatePersonBillReceiver = {
    type: BillReceiversType.PRIVATE_PERSON
    debitorNo: string
    name: string
    address: BillReceiverAddress
    endpoint: BillReceiverEndpoint
    isActive: boolean
    contact?: never
}

export type PublicInstitutionBillReceiver = {
    type: BillReceiversType.PUBLIC_INSTITUTION
    debitorNo: string
    name: string
    address: BillReceiverAddress
    endpoint: BillReceiverEndpoint
    isActive: boolean
    contact: Contact
    cvrNumber?: never
}

export type PrivateCompanyBillReceiver = {
    type: BillReceiversType.PRIVATE_COMPANY
    cvrNumber?: string
    debitorNo: string
    name: string
    address: BillReceiverAddress
    endpoint: BillReceiverEndpoint
    isActive: boolean
    contact: Contact
}

export function isPrivatePersonDetails(values: BillReceiversFormValuesType): values is PrivatePersonDetailsFormValues {
    return "name" in values && !("institutionName" in values) && !("companyName" in values)
}

export function isPublicInstitutionDetails(
    values: BillReceiversFormValuesType,
): values is PublicInstitutionDetailsFormValues {
    return "institutionName" in values && !("companyName" in values) && !("name" in values)
}

export function isPrivateCompanyDetails(
    values: BillReceiversFormValuesType,
): values is PrivateCompanyDetailsFormValues {
    return "companyName" in values && !("institutionName" in values) && !("name" in values)
}
